import PasswordInput from "@/components/PasswordInput";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Users, Ticket, ChartLine, BookOpen, Plus, Copy, Check, Receipt, Trash, DownloadSimple, Folder, GraduationCap, Stack, SquaresFour, Palette, Translate, QrCode, X, ShieldCheck, ShieldWarning, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { API_BASE, apiFetch } from "@/lib/api";
import LibraryPanel from "@/components/admin/LibraryPanel";
import CourseBuilderPanel from "@/components/admin/CourseBuilderPanel";
import IssuedCertificatesPanel from "@/components/admin/IssuedCertificatesPanel";
import WaiversPanel from "@/components/admin/WaiversPanel";
import FrontDeskPanel from "@/components/admin/FrontDeskPanel";
import PillarsPanel from "@/components/admin/PillarsPanel";
import BrandingPanel from "@/components/admin/BrandingPanel";
import WebsitePanel from "@/components/admin/WebsitePanel";
import TranslationsPanel from "@/components/admin/TranslationsPanel";
import TeamPanel from "@/components/admin/TeamPanel";
import MfaSecurityPanel from "@/components/admin/MfaSecurityPanel";
import RenewalsRadarPanel from "@/components/admin/RenewalsRadarPanel";
import BoardReportsPanel from "@/components/admin/BoardReportsPanel";
import ClassRosterPanel, { PdfModal, RenameStudentModal } from "@/components/admin/ClassRosterPanel";
import OtherClassesPanel from "@/components/admin/OtherClassesPanel";
import SchoolsPanel from "@/components/admin/SchoolsPanel";
import DemoRepsPanel from "@/components/admin/DemoRepsPanel";
import CoachInsightsPanel from "@/components/admin/CoachInsightsPanel";
import SchoolPurgeCard from "@/components/admin/SchoolPurgeCard";
import SchoolLogoCard from "@/components/admin/SchoolLogoCard";
import GradeMatrixPanel from "@/components/admin/GradeMatrixPanel";
import InvoicesPanel from "@/components/admin/InvoicesPanel";
import { AdminSearchBar } from "@/components/admin/AdminSearchBar";
import MessagingSettingsCard from "@/components/admin/MessagingSettingsCard";
import InstructorsPanel from "@/components/admin/InstructorsPanel";
import TeachersPanel from "@/components/admin/TeachersPanel";
import { TabBoard } from "@/components/admin/TabBoard";
import { NdaGate } from "@/components/NdaGate";
import { useFeatures } from "@/lib/features";
import FeedbackPanel from "@/components/admin/FeedbackPanel";
import AuditLogPanel from "@/components/admin/AuditLogPanel";
import { ScreenGuard } from "@/components/ScreenGuard";
import { usePillars } from "@/contexts/PillarsContext";
import { roleLabel, ASSIGNABLE_ROLES } from "@/lib/roles";

function bypassHoursLeft(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / 3600000));
}


export default function AdminPanelPage() {
  const { user } = useAuth();
  const { termPlural } = usePillars();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "users");
  // Keep the active tab in the URL so the header breadcrumb always knows where we are.
  const selectTab = (id) => {
    setTab(id);
    nav(`/admin?tab=${id}`, { replace: true });
  };
  useEffect(() => {  // legacy deep links (booking texts/emails, gcal callbacks) land on the Calendar tab
    if ((searchParams.get("booking") || searchParams.get("gcal")) && tab !== "calendar") setTab("calendar");
    // eslint-disable-next-line
  }, [searchParams]);

  const openCourseParam = searchParams.get("course") || null;
  // Sync tab with URL changes (e.g. jumping from a Users-table teacher link to the roster).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [searchParams]);
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [finance, setFinance] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const isOwner = user?.role === "super_admin";
  const isAdmin = ["super_admin", "team_dev"].includes(user?.role);
  const isTeamContent = user?.role === "team_content";
  const isL2 = user?.role === "level2_instructor";
  const feat = useFeatures();
  const isInstructor = ["super_admin", "level2_instructor", "level1_instructor", "team_dev"].includes(user?.role);
  const isSchoolStaff = ["teacher", "school_admin", "school_board_admin"].includes(user?.role);
  const isOrgAdmin = ["school_admin", "school_board_admin"].includes(user?.role);
  const canRoster = isAdmin || isSchoolStaff;
  const [mfaPromptDismissed, setMfaPromptDismissed] = useState(
    () => localStorage.getItem("sap_mfa_prompt_dismissed") === "1"
  );
  const showMfaPrompt = isAdmin && user && !user.mfa_enabled && !mfaPromptDismissed;
  const dismissMfaPrompt = () => { localStorage.setItem("sap_mfa_prompt_dismissed", "1"); setMfaPromptDismissed(true); };

  useEffect(() => {
    if (!user) return;
    if (!isInstructor && !canRoster && !isTeamContent) { nav("/dashboard"); return; }
    if (isTeamContent && !searchParams.get("tab")) setTab("courses");
    if (!isInstructor && canRoster && !searchParams.get("tab")) setTab(isOrgAdmin ? "users" : "roster");
    refresh();
    // eslint-disable-next-line
  }, [user]);

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const c = await api.get("/courses");
      setCourses(c.data);
      if (isInstructor || isOrgAdmin) {
        const reqs = [api.get("/admin/users")];
        if (isInstructor) reqs.push(api.get("/admin/tokens"));
        const [u, t] = await Promise.all(reqs);
        setUsers(u.data);
        if (t) setTokens(t.data);
      }
      if (isAdmin) {
        const [f, inv] = await Promise.all([
          api.get("/admin/financial-overview"),
          api.get("/admin/invoices"),
        ]);
        setFinance(f.data);
        setInvoiceList(inv.data);
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (!user) return null;

  const tabList = isInstructor ? [
    ...(isAdmin ? [{ id: "website", label: "Website" }, { id: "calendar", label: "Calendar" }, { id: "personal", label: "Personal Accounts" }] : []),
    { id: "users", label: isAdmin ? "School Users" : "Users" },
    { id: "tokens", label: "Seats" },
    { id: "library", label: "Library" },
    { id: "courses", label: "Courses" },
    ...(isAdmin ? [{ id: "matrix", label: "Lesson Matrix" }] : []),
    ...(canRoster ? [{ id: "roster", label: "My Classes" }, { id: "other-classes", label: "Other Classes In My School" }] : []),
    { id: "security", label: "Security" },
    ...(isAdmin ? [
      { id: "pillars", label: termPlural },
      { id: "branding", label: "Branding" },
      { id: "translations", label: "Translations" },
      { id: "schools", label: "School Boards/Schools" },
      { id: "demo-reps", label: "Demo Reps" },
      { id: "coach", label: "Coach Insights" },
      { id: "finance", label: "Financials" },
      { id: "invoices", label: "Money" },
      { id: "frontdesk", label: "Front Desk" },
      { id: "certificates", label: "Certificates" },
      { id: "waivers", label: "Waivers" },
      { id: "renewals", label: "Renewals" },
      { id: "reports", label: "Reports" },
      { id: "messaging", label: "Messaging" },
      { id: "teachers", label: "Teachers" },
      { id: "feedback", label: "Feedback" },
      { id: "audit", label: "Audit Log" },
    ] : []),
    ...(isOwner ? [{ id: "team", label: "Team" }] : []),
    ...(feat.instructor_features ? [{ id: "instructors", label: "Instructors" }] : []),
  ] : isTeamContent ? [
    { id: "courses", label: "Courses" },
    { id: "library", label: "Library" },
    { id: "translations", label: "Translations" },
  ] : [
    ...(isOrgAdmin ? [{ id: "users", label: "School Users" }] : []),
    ...(user?.role === "school_board_admin" ? [{ id: "schools", label: "School Boards/Schools" }, { id: "reports", label: "Reports" }, { id: "messaging", label: "Messaging" }] : []),
    ...(user?.role === "school_admin" ? [{ id: "school-logo", label: "My School" }] : []),
    ...(user?.can_edit_grade_matrix ? [{ id: "matrix", label: "Lesson Matrix" }] : []),
    { id: "roster", label: "My Classes" },
    { id: "other-classes", label: "Other Classes In My School" },
  ];

  return (
    <ScreenGuard>
    <div className="bg-grain min-h-screen">
      <NdaGate />
      <div className="max-w-6xl mx-auto px-5 py-8">
        <button
          data-testid="admin-back-to-dashboard"
          className="btn-ghost flex items-center gap-1.5 text-[#000000] hover:text-[#000000] mb-3 font-semibold"
          onClick={() => nav("/dashboard")}
        >
          <ArrowLeft size={17} weight="bold" /> Back to dashboard
        </button>
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}>
          <span className="text-xs uppercase tracking-[0.25em] text-[#000000] font-display">{user.name} · {roleLabel(user.role)}</span>
          <h1 className="text-4xl font-extrabold font-display">Operations Panel</h1>
          <p className="text-[#000000]">{isTeamContent ? "Edit lessons, French strings and French emails." : "Manage roster, tokens, courses & financials."}</p>
        </motion.div>

        <AdminSearchBar tabs={tabList} selectTab={selectTab} />

        {showMfaPrompt && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            data-testid="mfa-prompt-banner"
            className="mt-5 rounded-2xl border border-[#FDBA74] bg-[#FFF7ED] p-4 flex items-start gap-3"
          >
            <ShieldWarning size={26} weight="duotone" color="#EA580C" className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold font-display text-[#7C2D12]">Secure your admin account</div>
              <p className="text-sm text-[#9A3412]">You haven't set up two-factor authentication yet. It takes about a minute and protects the platform's highest-privilege account.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="mfa-prompt-enable" className="btn-primary text-xs" style={{padding:"5px 14px"}} onClick={() => selectTab("security")}>Enable 2FA</button>
              <button data-testid="mfa-prompt-dismiss" className="btn-ghost text-xs text-[#9A3412]" onClick={dismissMfaPrompt}>Dismiss</button>
            </div>
          </motion.div>
        )}

        <TabBoard tabs={tabList} tab={tab} selectTab={selectTab} />

        {err && <div className="text-[#FF5100] mb-3">{err}</div>}
        {loading && <div className="text-[#000000]">Loading...</div>}

        {tab === "users" && (
          <UsersPanel users={users} courses={courses} onRefresh={refresh} canCreateL1={isAdmin || isL2} role={user.role} myId={user.id} />
        )}
        {tab === "personal" && isAdmin && (
          <UsersPanel key="personal" personalOnly users={users} courses={courses} onRefresh={refresh} canCreateL1={false} role={user.role} myId={user.id} />
        )}
        {tab === "tokens" && (
          <TokensPanel tokens={tokens} />
        )}
        {tab === "courses" && (
          <CourseBuilderPanel courses={courses} onRefresh={refresh} canDelete={isAdmin} initialOpenCourseId={openCourseParam} />
        )}
        {tab === "library" && (
          <LibraryPanel canDelete={isAdmin} />
        )}
        {tab === "security" && <MfaSecurityPanel />}
        {tab === "roster" && canRoster && <ClassRosterPanel courses={courses} />}
        {tab === "other-classes" && canRoster && <OtherClassesPanel />}
        {tab === "schools" && (isAdmin || user?.role === "school_board_admin") && <SchoolsPanel />}
        {tab === "demo-reps" && isAdmin && <DemoRepsPanel />}
        {tab === "coach" && isAdmin && <CoachInsightsPanel />}
        {tab === "school-logo" && user?.role === "school_admin" && (<><SchoolLogoCard /><SchoolPurgeCard user={user} /></>)}
        {tab === "matrix" && (user?.can_edit_grade_matrix || isAdmin) && <GradeMatrixPanel />}
        {tab === "renewals" && isAdmin && <RenewalsRadarPanel />}
        {tab === "reports" && (isAdmin || user?.role === "school_board_admin") && <BoardReportsPanel user={user} />}
        {tab === "messaging" && (isAdmin || user?.role === "school_board_admin") && <MessagingSettingsCard />}
        {tab === "instructors" && (isAdmin || isInstructor) && feat.instructor_features && <InstructorsPanel />}
        {tab === "teachers" && isAdmin && <TeachersPanel />}
        {tab === "feedback" && isAdmin && <FeedbackPanel />}
        {tab === "finance" && finance && <FinancePanel finance={finance} />}
        {tab === "invoices" && isAdmin && <InvoicesPanel onRefresh={refresh} />}
        {tab === "frontdesk" && isAdmin && <FrontDeskPanel />}
        {tab === "certificates" && isAdmin && <IssuedCertificatesPanel />}
        {tab === "waivers" && isAdmin && <WaiversPanel />}
        {tab === "pillars" && isAdmin && <PillarsPanel />}
        {tab === "branding" && isAdmin && <BrandingPanel />}
        {tab === "website" && isAdmin && <WebsitePanel />}
        {tab === "calendar" && isAdmin && <WebsitePanel section="calendar" />}
        {tab === "translations" && (isAdmin || isTeamContent) && <TranslationsPanel contentOnly={isTeamContent} />}
        {tab === "team" && isOwner && <TeamPanel />}
        {tab === "audit" && isAdmin && <AuditLogPanel />}
      </div>
    </div>
    </ScreenGuard>
  );
}

function EditUserModal({ user, onSave, onClose }) {
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.first_name || (user.name || "").split(" ")[0] || "");
  const [lastName, setLastName] = useState(user.last_name ?? (user.name || "").split(" ").slice(1).join(" "));
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = { email, first_name: firstName.trim(), last_name: lastName.trim() || null };
      if (password) payload.password = password;
      await onSave(user.id, payload);
    } catch (ex) { setErr(formatApiError(ex)); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="edit-user-modal">
      <form className="bg-[#F4F5F7] rounded-2xl border border-[#C4C8CE] max-w-sm w-full p-5 flex flex-col gap-3" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-start justify-between">
          <h3 className="font-display font-bold text-lg">Edit user</h3>
          <button type="button" className="btn-ghost p-1 text-[#000000]" onClick={onClose} data-testid="edit-user-close"><X size={18} /></button>
        </div>
        <label className="text-xs uppercase tracking-wider text-[#000000]">Email
          <input data-testid="edit-user-email" className="input-base mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="text-xs uppercase tracking-wider text-[#000000]">First name
          <input data-testid="edit-user-first-name" className="input-base mt-1" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        </label>
        <label className="text-xs uppercase tracking-wider text-[#000000]">Last name
          <input data-testid="edit-user-last-name" className="input-base mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </label>
        <label className="text-xs uppercase tracking-wider text-[#000000]">New password <span className="normal-case text-[#000000]">(leave blank to keep current)</span>
          <PasswordInput testid="edit-user-password" className="input-base" wrapperClassName="mt-1" placeholder="••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {err && <div className="text-[#FF5100] text-sm">{err}</div>}
        <div className="flex gap-2 mt-1">
          <button data-testid="edit-user-save" className="btn-primary flex-1" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function RolesModal({ user, onSave, onClose }) {
  const initial = user.roles && user.roles.length ? user.roles : [user.role];
  const [selected, setSelected] = useState(initial);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (r) => setSelected(s => s.includes(r) ? s.filter(x => x !== r) : [...s, r]);

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { setErr("Select at least one role"); return; }
    setSaving(true); setErr("");
    try {
      await onSave(user.id, selected);
    } catch (ex) { setErr(formatApiError(ex)); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="roles-modal">
      <form className="bg-[#F4F5F7] rounded-2xl border border-[#C4C8CE] max-w-sm w-full p-5 flex flex-col gap-3" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Manage roles</h3>
            <p className="text-xs text-[#000000]">{user.name} · assign one or more roles</p>
          </div>
          <button type="button" className="btn-ghost p-1 text-[#000000]" onClick={onClose} data-testid="roles-modal-close"><X size={18} /></button>
        </div>
        <div className="flex flex-col gap-1.5">
          {ASSIGNABLE_ROLES.map(r => (
            <label key={r} className="flex items-center gap-2.5 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#E4E4E7]">
              <input
                type="checkbox"
                data-testid={`roles-modal-option-${r}`}
                className="w-4 h-4 accent-[#0A0D14] cursor-pointer"
                checked={selected.includes(r)}
                onChange={() => toggle(r)}
              />
              <span className="font-medium">{roleLabel(r)}</span>
            </label>
          ))}
        </div>
        <p className="text-[11px] text-[#000000]">The first selected role becomes the primary. Users with 2+ roles get a context switcher in the header.</p>
        {err && <div className="text-[#FF5100] text-sm">{err}</div>}
        <div className="flex gap-2 mt-1">
          <button data-testid="roles-modal-save" className="btn-primary flex-1" disabled={saving}>{saving ? "Saving…" : "Save roles"}</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function QuickLoginModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(info.url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) {}
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="quick-login-modal">
      <div className="bg-[#F4F5F7] rounded-2xl border border-[#C4C8CE] max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-display font-bold text-lg">Quick-login</h3>
          <button className="btn-ghost p-1 text-[#000000]" onClick={onClose} data-testid="quick-login-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-[#000000] mb-3">Open this on any device to sign in as <span className="font-semibold text-[#000000]">{info.name}</span> ({info.role.replace("_", " ")}) in an <span className="font-semibold">isolated tab</span> — your admin session stays put.</p>
        <div className="flex justify-center bg-white rounded-xl p-4 border border-[#C4C8CE] mb-3">
          <QRCodeSVG value={info.url} size={190} level="M" data-testid="quick-login-qr" />
        </div>
        <div className="flex gap-2 mb-3">
          <input readOnly value={info.url} className="input-base text-xs flex-1 min-w-0" style={{ padding: "6px 10px" }} data-testid="quick-login-url" onFocus={(e) => e.target.select()} />
          <button className="btn-secondary text-xs flex items-center gap-1" style={{ padding: "6px 12px" }} onClick={copy} data-testid="quick-login-copy">
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <a href={info.url} target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center block" data-testid="quick-login-open">Open in new tab</a>
        <p className="text-[11px] text-[#000000] mt-2">Link stays valid until you unset the Test flag or deactivate the account.</p>
      </div>
    </div>
  );
}


function UsersPanel({ users, courses, onRefresh, canCreateL1, role, myId, personalOnly = false }) {
  const featRoles = useFeatures();
  const nav = useNavigate();
  const isBoardAdmin = role === "school_board_admin";
  const isSchoolAdmin = role === "school_admin";
  const isOrgAdmin = isBoardAdmin || isSchoolAdmin;
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: personalOnly ? "public_student" : (isOrgAdmin ? "teacher" : "public_student"), payout_percentage: 0, is_test: false, school_id: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [grantFor, setGrantFor] = useState(null); // userId
  const [grantCourseId, setGrantCourseId] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [boardFilter, setBoardFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const isAdmin = role === "super_admin";

  const canManageUser = (u) => {
    if (u.id === myId || u.is_student_token) return false;
    if (isAdmin) return u.role !== "super_admin";
    if (isBoardAdmin) return ["school_admin", "teacher"].includes(u.role);
    if (isSchoolAdmin) return u.role === "teacher";
    return false;
  };
  const canEditUser = (u) => !u.is_student_token && ((isAdmin && u.role !== "super_admin") || (isOrgAdmin && canManageUser(u)));
  const canQuickLogin = (u) => u.is_student_token ? (isAdmin || isOrgAdmin) : canEditUser(u);

  const [mySchools, setMySchools] = useState([]);
  const [pendingSeats, setPendingSeats] = useState(null);
  useEffect(() => {
    if (isBoardAdmin) {
      api.get("/schools").then(r => setMySchools(r.data)).catch(() => {});
      api.get("/admin/billing/pending").then(r => setPendingSeats(r.data)).catch(() => {});
    }
  }, [isBoardAdmin]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const displayedUsers = React.useMemo(() => {
    let list = showArchived ? archivedUsers : users;
    if (personalOnly) list = list.filter(u => u.role === "public_student");
    else if (role === "super_admin") list = list.filter(u => u.role !== "public_student");
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(u =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.student_id || "").toLowerCase().includes(q) ||
      (u.class_name || "").toLowerCase().includes(q) ||
      (u.school_name || "").toLowerCase().includes(q) ||
      (u.school_board_name || "").toLowerCase().includes(q) ||
      (u.teacher_name || "").toLowerCase().includes(q)
    );
    if (roleFilter !== "all") list = list.filter(u => roleFilter === "student"
      ? (u.role === "public_student" || u.role === "anonymous_token_student")
      : u.role === roleFilter);
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === "xp" || sortKey === "streak" || sortKey === "progress_percent") { av = av ?? -1; bv = bv ?? -1; return (av - bv) * dir; }
      return String(av || "").localeCompare(String(bv || "")) * dir;
    });
    // Default (unfiltered/unsorted) view: pin the signed-in admin to the top, then any other
    // super admins (e.g. backup admin). Any search, role filter, or manual sort disables pinning.
    const isDefaultView = !q && roleFilter === "all" && schoolFilter === "all" && boardFilter === "all" && sortKey === "name" && sortDir === "asc" && !showArchived;
    if (!isDefaultView) return sorted;
    const rank = (u) => u.id === myId ? 0
      : (u.role === "super_admin" || (u.roles || []).includes("super_admin")) ? 1 : 2;
    return sorted.sort((a, b) => rank(a) - rank(b));
  }, [showArchived, archivedUsers, users, search, roleFilter, schoolFilter, boardFilter, sortKey, sortDir, myId, personalOnly]);

  const schoolOptions = React.useMemo(
    () => [...new Set((showArchived ? archivedUsers : users).map(u => u.school_name).filter(Boolean))].sort(),
    [users, archivedUsers, showArchived]);
  const boardOptions = React.useMemo(
    () => [...new Set((showArchived ? archivedUsers : users).map(u => u.school_board_name).filter(Boolean))].sort(),
    [users, archivedUsers, showArchived]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Name", "Email", "Student ID", "Class", "Role", "Board", "School", "Teacher", "Progress %", "Passed", "Coins", "Streak"].map(esc).join(",")];
    displayedUsers.forEach(u => rows.push([
      u.name, u.email, u.student_id, u.class_name, u.role, u.school_board_name, u.school_name, u.teacher_name,
      u.progress_percent ?? "", (u.progress_percent ?? 0) >= 100 ? "Yes" : "No", u.xp ?? 0, u.streak ?? 0,
    ].map(esc).join(",")));
    const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const SortHead = ({ label, k }) => (
    <th className="p-2 cursor-pointer select-none" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      {label}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const canDeactivate = (u) => (isAdmin || (canCreateL1 && u.created_by_id === myId) || (isOrgAdmin && canManageUser(u))) && u.role !== "super_admin" && u.id !== myId;

  const toggleSelect = (id) => setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); setBulkConfirm(false); };

  const deactivateSelected = async () => {
    const targets = displayedUsers.filter(u => selectedIds.includes(u.id) && canDeactivate(u));
    if (targets.length === 0) return;
    setErr(""); setBulkBusy(true);
    try {
      for (const t of targets) {
        await api.post(`/admin/users/${t.id}/deactivate`);
      }
      exitSelectMode();
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const roles = (personalOnly
    ? ["public_student"]
    : role === "super_admin"
    ? ["school_board_admin", "school_admin", "teacher", "level2_instructor", "level1_instructor", "public_student"]
    : role === "school_board_admin"
      ? ["teacher", "school_admin"]
      : role === "school_admin"
        ? ["teacher"]
        : role === "level2_instructor"
          ? ["level1_instructor", "public_student"]
          : ["public_student"]).filter((r) => featRoles.instructor_features || !r.includes("instructor"));

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form };
      if (!payload.school_id) delete payload.school_id;
      const { data: created } = await api.post("/admin/users", payload);
      if (payload.role !== "public_student") {
        if (created?.welcome_email_sent) toast.success(`Welcome email sent to ${payload.email}`);
        else toast.warning("User created, but the welcome email could not be sent — share the login details manually.");
      }
      setShow(false); setForm({ email: "", password: "", first_name: "", last_name: "", role: roles[0], payout_percentage: 0, is_test: false, school_id: "" });
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const certify = async (id) => {
    try { await api.post(`/admin/users/${id}/certify-level1`); onRefresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const [quickLogin, setQuickLogin] = useState(null); // { name, email, role, url }
  const [editUser, setEditUser] = useState(null); // user being edited
  const [rolesUser, setRolesUser] = useState(null); // user whose roles are being edited

  const saveRoles = async (id, newRoles) => {
    await api.patch(`/admin/users/${id}/roles`, { roles: newRoles });
    setRolesUser(null);
    onRefresh();
  };

  const saveEdit = async (id, payload) => {
    await api.patch(`/admin/users/${id}`, payload);
    setEditUser(null);
    onRefresh();
  };

  const toggleTest = async (u) => {
    setErr("");
    try { await api.post(`/admin/users/${u.id}/test-flag`, { is_test: !u.is_test }); onRefresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const toggleMfaBypass = async (u) => {
    const turnOn = !u.mfa_force_disabled;
    const msg = turnOn
      ? `Bypass two-factor auth for ${u.name}?\n\nThey'll sign in with just email + password until you turn this back off. Use only for emergency recovery.`
      : `Re-enable two-factor auth for ${u.name}?`;
    if (!window.confirm(msg)) return;
    setErr("");
    try { await api.post(`/admin/users/${u.id}/mfa-bypass`, { enabled: turnOn }); onRefresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const [pdf, setPdf] = useState(null);
  const [renameStudent, setRenameStudent] = useState(null);
  const openStudentDoc = async (u, kind, label) => {
    setErr("");
    try {
      const { data } = await api.get(`/classes/${u.class_id}/students/${u.id}/${kind}?format=png`, { responseType: "blob" });
      setPdf({ url: URL.createObjectURL(new Blob([data], { type: "image/png" })), title: `${u.name} — ${label}` });
    } catch (e) {
      let msg = `Couldn't open ${label}.`;
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      setErr(msg);
    }
  };
  const openUserCert = async (u) => {
    setErr("");
    try {
      const { data } = await api.get(`/admin/users/${u.id}/certificate?course_id=${u.passed_course_id}&format=png`, { responseType: "blob" });
      setPdf({ url: URL.createObjectURL(new Blob([data], { type: "image/png" })), title: `${u.name} — Certificate` });
    } catch (e) {
      let msg = "Couldn't open certificate.";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      setErr(msg);
    }
  };
  const passStudent = async (u) => {
    setErr("");
    try {
      const { data } = await api.post(`/classes/${u.class_id}/students/${u.id}/pass-all`);
      toast.success(`${u.name} passed (${data.passed} lessons). "Undo pass" restores their real progress.`);
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const restoreStudent = async (u) => {
    setErr("");
    try {
      await api.post(`/classes/${u.class_id}/students/${u.id}/restore-progress`);
      toast.success(`${u.name} restored to their real progress`);
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const openQuickLogin = async (u) => {
    setErr("");
    try {
      if (u.is_student_token) {
        const { data } = await api.post(`/classes/${u.class_id}/students/${u.id}/login-link`);
        const url = `${window.location.origin}/impersonate#token=${encodeURIComponent(data.login_token)}`;
        setQuickLogin({ name: data.name || u.name, email: u.student_id || "", role: "student", url });
        return;
      }
      const { data } = await api.post(`/admin/users/${u.id}/impersonation-token`);
      const url = `${window.location.origin}/impersonate#token=${encodeURIComponent(data.token)}`;
      setQuickLogin({ name: data.name, email: data.email, role: data.role, url });
    } catch (e) { setErr(formatApiError(e)); }
  };

  const grantCourse = async (userId, courseId) => {
    if (!courseId) return;
    setErr("");
    try {
      await api.post(`/admin/users/${userId}/grant-course`, { course_id: courseId });
      setGrantFor(null); setGrantCourseId("");
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const [confirmReset, setConfirmReset] = useState(null); // user id awaiting 2nd click
  const resetCourse = async (u) => {
    const courseId = courses.length === 1 ? courses[0].id : null;
    if (!courseId) { toast.error("Multiple courses — not yet supported here."); return; }
    // Two-click confirm (native confirm() is unreliable in installed PWAs).
    if (confirmReset !== u.id) {
      setConfirmReset(u.id);
      setTimeout(() => setConfirmReset(c => (c === u.id ? null : c)), 4000);
      return;
    }
    setConfirmReset(null);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-course`, { course_id: courseId });
      toast.success(`${data.course} reset for ${u.name} (${data.lessons_cleared} lessons cleared)`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [confirmResetAll, setConfirmResetAll] = useState(null); // user id awaiting 2nd click
  const resetAllProgress = async (u) => {
    if (confirmResetAll !== u.id) {
      setConfirmResetAll(u.id);
      setTimeout(() => setConfirmResetAll(c => (c === u.id ? null : c)), 4000);
      return;
    }
    setConfirmResetAll(null);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-progress`);
      toast.success(`Full reset for ${data.name} — lessons, quizzes, coins, gems, streak & perks wiped (${data.lessons_cleared} lessons cleared)`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleMatrixAccess = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/matrix-access`, { enabled: !u.can_edit_grade_matrix });
      toast.success(data.enabled ? `${u.name} can now edit the lesson matrix` : `Matrix access revoked for ${u.name}`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadArchived = async () => {
    setLoadingArchived(true);
    try {
      const { data } = await api.get("/admin/users", { params: { archived: true } });
      setArchivedUsers(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoadingArchived(false); }
  };

  const toggleArchived = async () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) await loadArchived();
  };

  const restore = async (u) => {
    setErr("");
    try { await api.post(`/admin/users/${u.id}/restore`); await loadArchived(); onRefresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const permanentDelete = async (u) => {
    setErr("");
    try { await api.delete(`/admin/users/${u.id}/permanent`); setConfirmDeleteId(null); await loadArchived(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  // Hard delete straight from the active list: archive first (backend requires it),
  // then wipe for good — frees the email immediately.
  const hardDelete = async (u) => {
    setErr("");
    try {
      if (!u.is_deleted) await api.post(`/admin/users/${u.id}/deactivate`);
      await api.delete(`/admin/users/${u.id}/permanent`);
      setConfirmDeleteId(null);
      toast.success(`${u.email || u.name} permanently deleted — email freed up.`);
      onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-xl font-bold font-display whitespace-nowrap">{personalOnly ? "Personal Accounts" : (role === "super_admin" || isOrgAdmin) ? "School Users" : "Users"} ({displayedUsers.length}{showArchived ? " archived" : ""})</h2>
        <div className="flex flex-wrap items-center gap-2">
          {!showArchived && !selectMode && (
            <button
              data-testid="bulk-deactivate-toggle"
              title="Select accounts to deactivate"
              className="btn-secondary text-xs leading-none flex items-center"
              style={{padding:"6px 12px"}}
              onClick={() => setSelectMode(true)}
            >
              <Trash size={16} weight="bold" />
            </button>
          )}
          {selectMode && (
            <>
              <button
                data-testid="bulk-deactivate-confirm"
                className="btn-secondary text-xs leading-none"
                style={{padding:"6px 14px",color:"#B91C1C",borderColor:"#FCA5A5",opacity: selectedIds.length ? 1 : 0.5}}
                disabled={!selectedIds.length}
                onClick={() => setBulkConfirm(true)}
              >
                Deactivate selected ({selectedIds.length})
              </button>
              <button data-testid="bulk-deactivate-cancel" className="btn-ghost text-xs text-[#000000]" onClick={exitSelectMode}>Cancel</button>
            </>
          )}
          <button
            data-testid="toggle-archived-users"
            className="btn-secondary text-xs leading-none flex items-center gap-1.5"
            style={{padding:"6px 14px"}}
            onClick={() => { exitSelectMode(); toggleArchived(); }}
          >
            {showArchived ? "← Active users" : "Show archived"}
          </button>
          {!showArchived && !selectMode && (
            <button data-testid="create-user-button" className="btn-primary flex items-center gap-1 whitespace-nowrap" onClick={() => setShow(true)}>
              <Plus size={16} weight="bold" /> Create user
            </button>
          )}
        </div>
      </div>

      {isBoardAdmin && pendingSeats && pendingSeats.total > 0 && (
        <div data-testid="pending-seats-banner" className="mb-4 p-3 rounded-2xl border border-[#BEF264] bg-[#F7FEE7] text-sm text-[#3F6212]">
          <b>{pendingSeats.total}</b> student seat{pendingSeats.total === 1 ? "" : "s"} added since your last invoice — they'll appear on your board's next bill.
        </div>
      )}

      {bulkConfirm && (
        <div data-testid="bulk-confirm-banner" className="mb-4 p-4 rounded-2xl border" style={{background:"#FEF2F2",borderColor:"#FCA5A5"}}>
          <div className="text-sm text-[#7F1D1D] font-semibold mb-1">
            Deactivate {selectedIds.length} account{selectedIds.length === 1 ? "" : "s"}?
          </div>
          <div className="text-xs text-[#B91C1C] mb-3">
            {displayedUsers.filter(u => selectedIds.includes(u.id) && canDeactivate(u)).map(u => u.name).join(", ")}
            {" — "}they'll be blocked from logging in and hidden from leaderboards. Data is preserved; restore anytime from "Show archived".
          </div>
          <div className="flex gap-2">
            <button
              data-testid="bulk-confirm-yes"
              className="btn-secondary text-xs leading-none"
              style={{padding:"6px 14px",background:"#B91C1C",color:"#FFFFFF",borderColor:"#B91C1C"}}
              disabled={bulkBusy}
              onClick={deactivateSelected}
            >
              {bulkBusy ? "Deactivating…" : `Yes, deactivate ${selectedIds.length}`}
            </button>
            <button data-testid="bulk-confirm-no" className="btn-ghost text-xs text-[#000000]" onClick={() => setBulkConfirm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {show && (
        <form onSubmit={create} className="mb-4 p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] grid sm:grid-cols-2 gap-3">
          <input data-testid="new-user-first-name" className="input-base" placeholder="First name" required value={form.first_name} onChange={(e)=>setForm({...form,first_name:e.target.value})} />
          <input data-testid="new-user-last-name" className="input-base" placeholder="Last name" value={form.last_name} onChange={(e)=>setForm({...form,last_name:e.target.value})} />
          <input data-testid="new-user-email" className="input-base" type="email" placeholder="Email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
          <PasswordInput testid="new-user-password" className="input-base" placeholder="Password (min 6)" required minLength={6} value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} />
          <select data-testid="new-user-role" className="input-base" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})}>
            {roles.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
          {isBoardAdmin && (
            <select data-testid="new-user-school" className="input-base" required={form.role === "school_admin"} value={form.school_id} onChange={(e)=>setForm({...form,school_id:e.target.value})}>
              <option value="">{form.role === "school_admin" ? "Pick a school (required)" : "No school yet (assign later)"}</option>
              {mySchools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {isSchoolAdmin && (
            <div className="text-xs text-[#3F6212] self-center">New teachers are added to your school automatically.</div>
          )}
          {form.role.includes("instructor") && (
            <input data-testid="new-user-payout" className="input-base" type="number" min="0" max="100" step="1" placeholder="Payout %" value={form.payout_percentage} onChange={(e)=>setForm({...form,payout_percentage:Number(e.target.value)})} />
          )}
          {isAdmin && (
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-[#3F3F46] cursor-pointer">
              <input data-testid="new-user-is-test" type="checkbox" className="w-4 h-4 accent-[#84CC16]" checked={form.is_test} onChange={(e)=>setForm({...form,is_test:e.target.checked})} />
              <span><span className="font-semibold">Test account</span> — enable a Super-Admin quick-login link &amp; QR (for troubleshooting; never shown to real students)</span>
            </label>
          )}
          {err && <div className="text-[#FF5100] sm:col-span-2 text-sm">{err}</div>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="submit-new-user" type="submit" className="btn-primary" disabled={busy}>{busy?"Saving...":"Create"}</button>
            <button type="button" className="btn-secondary" onClick={()=>setShow(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          data-testid="user-search-input"
          className="input-base text-sm"
          style={{padding:"6px 12px",maxWidth:"260px"}}
          placeholder="Search name, email, school or board…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          data-testid="user-role-filter"
          className="input-base text-sm"
          style={{padding:"6px 12px",width:"auto",display: personalOnly ? "none" : undefined}}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All roles</option>
          {isAdmin && (
            <>
              <option value="super_admin">Super Admins</option>
              <option value="school_board_admin">Board Admins</option>
              {featRoles.instructor_features && (
                <>
                  <option value="level2_instructor">Level 2 Instructors</option>
                  <option value="level1_instructor">Level 1 Instructors</option>
                </>
              )}
            </>
          )}
          {(isAdmin || isBoardAdmin) && <option value="school_admin">School Admins</option>}
          <option value="teacher">Teachers</option>
          <option value="student">Students</option>
        </select>
        {boardOptions.length > 0 && !personalOnly && (
          <select
            data-testid="user-board-filter"
            className="input-base text-sm"
            style={{padding:"6px 12px",width:"auto"}}
            value={boardFilter}
            onChange={(e) => setBoardFilter(e.target.value)}
          >
            <option value="all">All boards</option>
            {boardOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
        {schoolOptions.length > 0 && !personalOnly && (
          <select
            data-testid="user-school-filter"
            className="input-base text-sm"
            style={{padding:"6px 12px",width:"auto"}}
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
          >
            <option value="all">All schools</option>
            {schoolOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {(search || roleFilter !== "all" || schoolFilter !== "all" || boardFilter !== "all") && (
          <button data-testid="user-filter-reset" className="btn-ghost text-xs text-[#000000]" onClick={() => { setSearch(""); setRoleFilter("all"); setSchoolFilter("all"); setBoardFilter("all"); }}>Reset</button>
        )}
        <button data-testid="user-export-csv" className="btn-secondary text-xs ml-auto" onClick={exportCsv} title="Download the currently filtered list as a spreadsheet">
          Export CSV ({displayedUsers.length})
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
              {selectMode && <th className="p-2 w-8"></th>}
              <SortHead label="Name" k="name" /><SortHead label="Email" k="email" /><SortHead label="Role" k="role" /><SortHead label="Board" k="school_board_name" /><SortHead label="School" k="school_name" /><SortHead label="Teacher" k="teacher_name" /><SortHead label="Progress" k="progress_percent" /><SortHead label="XP" k="xp" /><SortHead label="Streak" k="streak" /><th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map(u => (
              <tr key={u.id} className={"border-t border-[#C4C8CE]" + (showArchived ? " opacity-70" : "") + (selectMode && selectedIds.includes(u.id) ? " bg-[#FEE2E2]" : "")} data-testid={`user-row-${u.id}`}>
                {selectMode && (
                  <td className="p-2">
                    {canDeactivate(u) ? (
                      <input
                        type="checkbox"
                        data-testid={`select-user-${u.id}`}
                        className="w-4 h-4 accent-[#B91C1C] cursor-pointer"
                        checked={selectedIds.includes(u.id)}
                        onChange={() => toggleSelect(u.id)}
                      />
                    ) : null}
                  </td>
                )}
                <td className="p-2 font-semibold">{u.name}{u.id === myId && <span className="ml-1 text-[10px] text-[#000000]">(you)</span>}{u.mfa_enabled && <span title="Two-factor enabled" data-testid={`mfa-badge-${u.id}`} className="ml-1.5 inline-flex align-middle"><ShieldCheck size={15} weight="fill" color="#84CC16" /></span>}{u.mfa_force_disabled && <span data-testid={`mfa-bypass-badge-${u.id}`} className="ml-1.5 chip font-bold" style={{background:"#FEE2E2",color:"#B91C1C",border:"1px solid #FCA5A5",padding:"1px 7px",fontSize:"0.6rem"}}>2FA BYPASSED{bypassHoursLeft(u.mfa_bypass_expires_at) != null ? ` · ${bypassHoursLeft(u.mfa_bypass_expires_at)}h LEFT` : ""}</span>}{u.is_certified_level1 && <span className="ml-2 chip font-bold" style={{background:"#CCFF00",color:"#0A0D14",border:"1px solid #0A0D14",padding:"1px 8px",fontSize:"0.65rem"}}>Certified L1</span>}</td>
                <td className="p-2 text-[#000000]">{u.is_student_token ? <>ID <b className="text-[#000000]">{u.student_id}</b>{u.class_name ? ` · ${u.class_name}` : ""}</> : u.email}</td>
                <td className="p-2 text-[#000000]">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles && u.roles.length ? u.roles : [u.role]).map(r => (
                      <span
                        key={r}
                        className={"chip text-[0.65rem] font-semibold " + (r === u.role ? "" : "opacity-70")}
                        style={r === u.role
                          ? { background: "#0A0D14", color: "#CCFF00", padding: "1px 8px" }
                          : { background: "#E4E4E7", color: "#000000", padding: "1px 8px" }}
                        title={r === u.role ? "Primary role" : "Additional role"}
                      >
                        {roleLabel(r)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-2 text-[#000000]" data-testid={`user-board-${u.id}`}>{u.school_board_name || "—"}</td>
                <td className="p-2 text-[#000000]" data-testid={`user-school-${u.id}`}>{u.school_name || "—"}</td>
                <td className="p-2 text-[#000000]" data-testid={`user-teacher-${u.id}`}>
                  {u.teacher_name && u.class_id ? (
                    <button
                      className="btn-ghost text-[#1E40AF] underline underline-offset-2 hover:text-[#000000] text-left p-0"
                      title="Open this student's class roster"
                      onClick={() => nav(`/admin?tab=roster&class=${u.class_id}`)}
                    >
                      {u.teacher_name}
                    </button>
                  ) : (u.teacher_name || "—")}
                </td>
                <td className="p-2" data-testid={`user-progress-${u.id}`}>
                  {u.progress_percent != null ? (
                    (u.progress_percent ?? 0) >= 100 ? (
                      <span className="flex items-center gap-1 font-bold text-[#000000]"><CheckCircle size={15} weight="fill" color="#16A34A" /> Passed</span>
                    ) : (
                      <span className="text-[#000000]">{u.progress_percent ?? 0}%</span>
                    )
                  ) : <span className="text-[#000000]">—</span>}
                </td>
                <td className="p-2 text-[#FFD600] font-display font-bold">{u.xp || 0}</td>
                <td className="p-2 text-[#FF5100] font-display font-bold">{u.streak || 0}</td>
                <td className="p-2 flex items-center gap-1.5 flex-wrap">
                  {showArchived ? (
                    <>
                      <button data-testid={`restore-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => restore(u)}>Restore</button>
                      {isAdmin && (
                        confirmDeleteId === u.id ? (
                          <>
                            <button data-testid={`permanent-delete-confirm-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#B91C1C",color:"#FFFFFF",borderColor:"#B91C1C"}} onClick={() => permanentDelete(u)}>Confirm delete</button>
                            <button className="btn-ghost text-xs text-[#000000]" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                          </>
                        ) : (
                          <button data-testid={`permanent-delete-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#FEE2E2",color:"#B91C1C",borderColor:"#FCA5A5"}} onClick={() => setConfirmDeleteId(u.id)}>Permanently delete</button>
                        )
                      )}
                    </>
                  ) : (
                    <>
                      {canEditUser(u) && (
                        <button data-testid={`edit-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => setEditUser(u)}>Edit</button>
                      )}
                      {isAdmin && u.role !== "super_admin" && !u.is_student_token && (
                        <button data-testid={`roles-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => setRolesUser(u)}>Roles</button>
                      )}
                      {u.role === "level1_instructor" && !u.is_certified_level1 && canCreateL1 && featRoles.instructor_features && (
                        <button data-testid={`certify-button-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => certify(u.id)}>Certify L1</button>
                      )}
                      {isAdmin && u.role === "public_student" && (
                        grantFor === u.id ? (
                          <select
                            data-testid={`grant-course-select-${u.id}`}
                            className="input-base text-xs leading-none"
                            style={{padding:"3px 10px",width:"auto"}}
                            autoFocus
                            defaultValue=""
                            onChange={(e) => grantCourse(u.id, e.target.value)}
                            onBlur={() => setGrantFor(null)}
                          >
                            <option value="" disabled>Select a course…</option>
                            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                          </select>
                        ) : (
                          <button data-testid={`grant-button-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => setGrantFor(u.id)}>Enroll</button>
                        )
                      )}
                      {u.is_student_token && (
                        <div className="grid grid-cols-2 gap-1.5">
                          <button data-testid={`edit-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => setRenameStudent(u)}>Edit</button>
                          {(u.progress_percent ?? 0) >= 100 ? (
                            u.has_backup ? (
                              <button data-testid={`restore-progress-user-${u.id}`} title="Undo: back to their real progress" className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#DBEAFE",color:"#1E40AF",borderColor:"#93C5FD"}} onClick={() => restoreStudent(u)}>Undo pass</button>
                            ) : <span />
                          ) : (
                            <button data-testid={`pass-user-${u.id}`} title="Mark every lesson completed (their real progress is saved for undo)" className="btn-secondary text-xs leading-none flex items-center justify-center gap-1" style={{padding:"3px 12px",background:"#DCFCE7",color:"#166534",borderColor:"#86EFAC"}} onClick={() => passStudent(u)}>
                              <CheckCircle size={13} weight="bold" /> Pass
                            </button>
                          )}
                          <button data-testid={`idcard-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => openStudentDoc(u, "id-card", "ID card")}>ID Card</button>
                          {(u.progress_percent ?? 0) >= 100 ? (
                            <button data-testid={`cert-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px"}} onClick={() => openStudentDoc(u, "certificate", "Certificate")}>Cert</button>
                          ) : <span />}
                        </div>
                      )}
                      {!u.is_student_token && u.passed_course_id && (
                        <button data-testid={`cert-user-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#DCFCE7",color:"#166534",borderColor:"#86EFAC"}} onClick={() => openUserCert(u)}>Cert</button>
                      )}
                      {canQuickLogin(u) && (
                        <button data-testid={`quick-login-${u.id}`} className="btn-secondary text-xs leading-none flex items-center gap-1" style={{padding:"3px 12px",background:"#EEF7D6",color:"#3F6212",borderColor:"#BEF264"}} onClick={() => openQuickLogin(u)}>
                          <QrCode size={13} weight="bold" /> Quick-login
                        </button>
                      )}
                      {isAdmin && u.role !== "super_admin" && (
                        <button data-testid={`reset-course-user-${u.id}`} title="Function testing: wipe this person's lesson & quiz progress for the course (coins/streak stay)" className="btn-secondary text-xs leading-none" style={confirmReset === u.id ? {padding:"3px 12px",background:"#B91C1C",color:"#FFFFFF",borderColor:"#B91C1C"} : {padding:"3px 12px",background:"#FEE2E2",color:"#B91C1C",borderColor:"#FCA5A5"}} onClick={() => resetCourse(u)}>
                          {confirmReset === u.id ? "Confirm reset?" : "Reset course"}
                        </button>
                      )}
                      {isAdmin && u.role !== "super_admin" && (
                        <button data-testid={`reset-progress-user-${u.id}`} title="FULL wipe: lessons, quizzes, coins, gems, streak, shields & perks — a brand-new learner state" className="btn-secondary text-xs leading-none" style={confirmResetAll === u.id ? {padding:"3px 12px",background:"#7C2D12",color:"#FFFFFF",borderColor:"#7C2D12"} : {padding:"3px 12px",background:"#FFEDD5",color:"#9A3412",borderColor:"#FDBA74"}} onClick={() => resetAllProgress(u)}>
                          {confirmResetAll === u.id ? "Confirm FULL reset?" : "Reset ALL progress"}
                        </button>
                      )}
                      {isAdmin && u.role !== "super_admin" && u.id !== myId && !u.is_student_token && (
                        confirmDeleteId === u.id ? (
                          <>
                            <button data-testid={`hard-delete-confirm-${u.id}`} className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#B91C1C",color:"#FFFFFF",borderColor:"#B91C1C"}} onClick={() => hardDelete(u)}>Confirm delete forever</button>
                            <button className="btn-ghost text-xs text-[#000000]" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                          </>
                        ) : (
                          <button data-testid={`hard-delete-user-${u.id}`} title="Permanently delete this account and free up its email. Cannot be undone." className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#FEE2E2",color:"#B91C1C",borderColor:"#FCA5A5"}} onClick={() => setConfirmDeleteId(u.id)}>Delete forever</button>
                        )
                      )}
                      {isAdmin && ["school_board_admin", "school_admin"].includes(u.role) && (
                        <button data-testid={`matrix-access-${u.id}`} title="Grant/revoke the ability to edit the course grade matrix (no lesson-content access)" className="btn-secondary text-xs leading-none" style={u.can_edit_grade_matrix ? {padding:"3px 12px",background:"#E0E7FF",color:"#3730A3",borderColor:"#A5B4FC"} : {padding:"3px 12px"}} onClick={() => toggleMatrixAccess(u)}>
                          {u.can_edit_grade_matrix ? "Revoke matrix" : "Grant matrix"}
                        </button>
                      )}
                      {isAdmin && !u.is_student_token && (
                        u.mfa_force_disabled ? (
                          <button data-testid={`mfa-bypass-toggle-${u.id}`} title="Re-enable two-factor auth for this account" className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#DCFCE7",color:"#166534",borderColor:"#86EFAC"}} onClick={() => toggleMfaBypass(u)}>Re-enable 2FA</button>
                        ) : (
                          <button data-testid={`mfa-bypass-toggle-${u.id}`} title="Break-glass: let this account sign in without its 2FA code (auto-expires in 24h)" className="btn-secondary text-xs leading-none" style={{padding:"3px 12px",background:"#FEF3C7",color:"#92400E",borderColor:"#FCD34D"}} onClick={() => toggleMfaBypass(u)}>Skip 2FA</button>
                        )
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {displayedUsers.length === 0 && !loadingArchived && (
              <tr><td colSpan={selectMode ? 11 : 10} className="p-4 text-center text-[#000000]">{showArchived ? "No archived users." : "No users match your filters."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {quickLogin && <QuickLoginModal info={quickLogin} onClose={() => setQuickLogin(null)} />}
      {pdf && <PdfModal url={pdf.url} title={pdf.title} onClose={() => { URL.revokeObjectURL(pdf.url); setPdf(null); }} />}
      {renameStudent && <RenameStudentModal classId={renameStudent.class_id} student={renameStudent} onClose={() => setRenameStudent(null)} onSaved={() => { setRenameStudent(null); onRefresh(); }} />}
      {editUser && <EditUserModal user={editUser} onSave={saveEdit} onClose={() => setEditUser(null)} />}
      {rolesUser && <RolesModal user={rolesUser} onSave={saveRoles} onClose={() => setRolesUser(null)} />}
    </div>
  );
}

function TokensPanel({ tokens }) {
  const [allowances, setAllowances] = useState(null);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    api.get("/admin/billing/seat-blocks")
      .then(({ data }) => setAllowances((data.boards || []).filter(b => b.allowance > 0 || b.used > 0)))
      .catch(() => setAllowances([]));
  }, [tokens.length]);

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code); setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="card" data-testid="seat-allowances-card">
        <h2 className="text-xl font-bold font-display mb-1">Seat allowances by board</h2>
        <p className="text-sm text-[#000000] mb-3">
          Seats are consumed automatically as students are added to classes. Add prepaid blocks in
          <b> Money → Prepaid seat blocks</b> when a board purchases seats.
        </p>
        {allowances === null && <div className="text-[#000000] text-sm">Loading…</div>}
        {allowances !== null && allowances.length === 0 && (
          <div className="text-[#000000] text-sm italic" data-testid="seat-allowances-empty">No prepaid seat blocks yet.</div>
        )}
        {allowances !== null && allowances.length > 0 && (
          <div className="flex flex-col gap-2">
            {allowances.map(b => (
              <div key={b.board_id || "independent"} className="flex items-center gap-3 flex-wrap rounded-xl border border-[#C4C8CE] bg-white px-3 py-2" data-testid={`seat-allowance-${b.board_id || "independent"}`}>
                <div className="font-bold font-display min-w-[180px]">{b.board_name}</div>
                <div className="text-sm text-[#000000]">
                  <b className="text-[#000000]">{b.used.toLocaleString()}</b> of <b className="text-[#000000]">{b.allowance.toLocaleString()}</b> seats used
                  · <b className="text-[#3F6212]">{b.remaining.toLocaleString()} left</b>
                </div>
                <div className="h-2 rounded-full bg-[#ECEDEF] border border-[#C4C8CE] w-40 overflow-hidden">
                  <div className="h-full bg-[#84CC16]" style={{ width: `${b.allowance ? Math.min(100, (b.used / b.allowance) * 100) : 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-xl font-bold font-display mb-1">All seats ({tokens.length})</h2>
        <p className="text-sm text-[#000000] mb-3">One row per student — codes are generated when a student is added to a class.</p>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#FFFFFF]">
              <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
                <th className="p-2">Code</th><th className="p-2">Assigned to</th><th className="p-2">XP</th><th className="p-2">Streak</th><th className="p-2">Lessons</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.id} className="border-t border-[#C4C8CE]" data-testid={`token-row-${t.id}`}>
                  <td className="p-2 font-mono text-[#CCFF00]">{t.code}</td>
                  <td className="p-2">{t.school_name || "—"}</td>
                  <td className="p-2 text-[#FFD600] font-bold">{t.xp || 0}</td>
                  <td className="p-2 text-[#FF5100] font-bold">{t.streak || 0}</td>
                  <td className="p-2">{(t.completed_lesson_ids || []).length}</td>
                  <td className="p-2"><button data-testid={`copy-token-${t.id}`} className="btn-ghost" onClick={() => copy(t.code)}>{copied === t.code ? <Check size={16} weight="bold" /> : <Copy size={16} />}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StripeModeBadge() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/admin/stripe-status").then(r => setStatus(r.data)).catch(() => {});
  }, []);
  if (!status) return null;
  const live = status.mode === "live";
  const label = !status.configured ? "Stripe: not configured" : live ? "Stripe: LIVE" : "Stripe: TEST MODE";
  const cls = !status.configured
    ? "bg-red-100 text-red-700 border-red-300"
    : live
      ? "bg-lime-100 text-lime-800 border-lime-400"
      : "bg-amber-100 text-amber-800 border-amber-400";
  return (
    <div className={`sm:col-span-3 flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-semibold ${cls}`} data-testid="stripe-mode-badge">
      <span className={`w-2 h-2 rounded-full ${live ? "bg-lime-600" : "bg-amber-500"}`} />
      {label}
      {!live && status.configured && (
        <span className="font-normal text-xs opacity-80">— real cards will NOT be charged. Update STRIPE_API_KEY in Deployment Secrets to go live.</span>
      )}
    </div>
  );
}

function FinancePanel({ finance }) {
  const feat = useFeatures();
  return (
    <div className="grid sm:grid-cols-3 gap-3 mb-5">
      <StripeModeBadge />
      <Stat label="Users" value={finance.total_users} />
      <Stat label="Access tokens" value={finance.total_tokens} />
      <Stat label="Courses" value={finance.total_courses} />
      <Stat label="Revenue" value={`$${(finance.total_revenue_cents / 100).toFixed(2)}`} />
      {feat.instructor_features && <Stat label="Instructor payouts" value={`$${(finance.total_instructor_payouts_cents / 100).toFixed(2)}`} />}
      <Stat label="Transactions" value={finance.transactions_count} />
      <DiscountCapControl />
      {feat.instructor_features && (
      <div className="sm:col-span-3 card">
        <h2 className="text-xl font-bold font-display mb-3">Instructor payouts</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#000000] uppercase text-xs">
            <th className="p-2">Instructor</th><th className="p-2">Role</th><th className="p-2">% Split</th><th className="p-2">Balance</th>
          </tr></thead>
          <tbody>
            {finance.instructors_payouts.map(i => (
              <PayoutRow key={i.id} inst={i} />
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function PayoutRow({ inst }) {
  const [pct, setPct] = useState(inst.payout_percentage ?? 0);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = Number(pct) !== Number(inst.payout_percentage ?? 0);
  const save = async () => {
    const n = Math.max(0, Math.min(100, parseFloat(pct) || 0));
    setPct(n); setBusy(true);
    try { await api.patch(`/admin/users/${inst.id}`, { payout_percentage: n }); setSaved(true); setTimeout(() => setSaved(false), 1500); }
    catch (_) {} finally { setBusy(false); }
  };
  return (
    <tr className="border-t border-[#C4C8CE]" data-testid={`payout-row-${inst.id}`}>
      <td className="p-2 font-semibold">{inst.name}</td>
      <td className="p-2 text-[#000000]">{inst.role.replace("_", " ")}</td>
      <td className="p-2">
        <div className="flex items-center gap-1.5">
          <input data-testid={`payout-input-${inst.id}`} type="number" min={0} max={100} step={1} value={pct}
            onChange={(e) => setPct(e.target.value)} className="input-base w-16 text-center" style={{ padding: "4px 6px" }} />
          <span className="text-[#000000]">%</span>
          {dirty && <button data-testid={`payout-save-${inst.id}`} onClick={save} disabled={busy} className="btn-primary" style={{ padding: "3px 10px", fontSize: "12px" }}>Save</button>}
          {saved && <span className="text-xs text-[#166534] font-bold">Saved</span>}
        </div>
      </td>
      <td className="p-2 font-display font-bold text-[#000000]">${(inst.payout_balance_cents / 100).toFixed(2)}</td>
    </tr>
  );
}

function DiscountCapControl() {
  const [cap, setCap] = useState(25);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/instructor/promo-codes").then(({ data }) => setCap(data.max_discount_percent ?? 25)).catch(() => {});
  }, []);
  const save = async () => {
    const n = Math.max(1, Math.min(100, parseInt(cap, 10) || 25));
    setCap(n); setBusy(true);
    try { await api.patch("/admin/settings", { instructor_max_discount_percent: n }); setSaved(true); setTimeout(() => setSaved(false), 1500); }
    catch (_) {} finally { setBusy(false); }
  };
  return (
    <div className="sm:col-span-3 card flex flex-wrap items-center gap-3" data-testid="discount-cap-control">
      <div className="flex-1 min-w-[220px]">
        <div className="font-bold font-display">Instructor discount cap</div>
        <div className="text-xs text-[#000000]">Max % off an instructor may set on their own promo codes.</div>
      </div>
      <div className="flex items-center gap-2">
        <input data-testid="discount-cap-input" type="number" min={1} max={100} value={cap} onChange={(e) => setCap(e.target.value)} className="input-base w-20 text-center" />
        <span className="text-[#000000]">%</span>
        <button data-testid="discount-cap-save" onClick={save} disabled={busy} className="btn-primary text-sm">{saved ? "Saved" : "Save"}</button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-[0.2em] text-[#000000]">{label}</div>
      <div className="text-3xl font-extrabold font-display text-[#000000]">{value}</div>
    </div>
  );
}
